// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { isoDateSchema, uuidSchema } from '../common.js';
// Employee self-service (/orgs/:orgId/me/…). Every endpoint acts on the caller's own employee record in that
// organisation (the membership's employee link); no endpoint accepts an employee id.
export const selfMonthQuerySchema = z.object({ month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected yyyy-MM').optional() });
export const selfLeaveQuerySchema = z.object({ year: z.coerce.number().int().min(2000).max(2100).optional() });
/** POST /orgs/:orgId/me/leave — a request starts PENDING; HR approves or rejects it on the Leave page. */
export const selfLeaveRequestSchema = z.object({
    leaveTypeId: uuidSchema,
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    isHalfDay: z.boolean().optional(),
    halfDayPart: z.enum(['FIRST_HALF', 'SECOND_HALF']).optional(),
    reason: z.string().trim().min(3, 'Tell HR briefly why').max(1000),
    /**
     * Workforce controls §4: why the leave is applied for after the fact, at short notice or as an emergency — required per
     * the organisation's `leave.lateApplication` settings (the API answers EXPLANATION_REQUIRED); the approver reads it.
     */
    explanation: z.string().trim().max(1000).optional(),
    /** "This is an emergency": waives the advance notice (where the settings allow the mark; an emergency type always is one). */
    isEmergency: z.boolean().optional(),
}).superRefine((v, ctx) => {
    if (v.endDate < v.startDate)
        ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'endDate must be on/after startDate' });
    if (v.isHalfDay && v.endDate !== v.startDate)
        ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'A half day is a single date' });
});
/** Correction types an employee may request for their own day (status overrides stay with HR). */
export const SELF_CORRECTION_TYPES = ['ADD_PUNCH', 'EDIT_PUNCH', 'REMOVE_PUNCH'];
//# sourceMappingURL=self-service.js.map